import React, { useState, useEffect, useRef, useCallback } from "react";
import useTranslation from "next-translate/useTranslation";
import dynamic from "next/dynamic";
import ReconnectPopup from "../ReconnectPopup";
import GameButtons from "./gameButtons/GameButtons";
import ChatWindow from "../../components/common/chat-window";

// Dynamically import game-ended components to avoid loading game review code upfront
const GameEndedButtons = dynamic(
  () => import("./gameButtons/GameEndedButtons"),
  { ssr: false }
);
const PopupContainer = dynamic(() => import("./popups/PopupContainer"), {
  ssr: false,
});
import styles from "./styles/ChatWindow.module.css";
import GameInfo from "./GameInfo";
import GameAd from "./GameAd";

import { REGULAR_TIER, UNLIMITED_TIER } from "@chesshotel/constants";
import { VariantTypes } from "@chesshotel/constants";
import {
  useGamePlay,
  sendUserMove,
  setGameIdSeen,
  setPremove,
  clearPremove,
} from "../../redux/gamePlay";
import Head from "next/head";
import { useSettings } from "../../redux/settings";
import { useMembership } from "../../redux/membership";

import Moves from "./Moves";
import GameChat from "./GameChat";
import OpponentPlayer from "./player/OpponentPlayer";
import UserPlayer from "./player/UserPlayer";
import Board from "../../chessBoard/components/Board";
import { useRouter } from "next/router";
import GameSyncPopup from "./popups/GameSyncPopup";

enum Tabs {
  MOVE,
  CHAT,
}

export default function GamePlay() {
  const { t } = useTranslation("common");

  let {
    gameId,
    gameEnded,
    positions,
    positionIndex,
    moves,
    possibleMoves,
    premove,
    playingWhite,
    isUserTurn,
    displayGame,
    leaguePlay,
    status,
    result,
    leagueScoreResult,
    opponent,
    chatMessages,
    variantId,
  } = useGamePlay();

  const fen = positions && positions[positionIndex];

  const settings = useSettings();
  const { autoQueening, boardSize, isDesktop, premoves } = settings;
  const router = useRouter();
  const { tier } = useMembership();

  const [chatOpened, setChatOpened] = useState(false);
  const [showChatWindow, setShowChatWindow] = useState(false);
  const [unreadChatMessages, setUnreadChatMessages] = useState(0);
  const [selectedTab, setSelectedTab] = useState(Tabs.MOVE);
  const [showEndGamePopup, setEndGamePopup] = useState(false);

  const chatLength = useRef(chatMessages.length);
  const gameScrollRef = useRef<HTMLDivElement>(null);
  const chatOpenGameScrollTop = useRef(0);
  const chatOpenWindowScrollY = useRef(0);

  const newPlayer = false;
  const opponentUsername = opponent.displayName;
  const livePremovesEnabled =
    variantId === VariantTypes.VARIANT_CHESS &&
    !gameEnded &&
    !displayGame &&
    premoves &&
    !isUserTurn &&
    positionIndex === moves.length;

  const handlePremoveSet = useCallback(
    (move: any) => {
      setPremove({
        ...move,
        audit: {
          ...move.audit,
          source: move.audit?.source || "premove",
          queuedAt: Date.now(),
          queuedFen: fen,
          queuedTurn: moves.length,
          isUserTurnAtGesture: isUserTurn,
          allowPremovesAtGesture: livePremovesEnabled,
          positionIndex,
          movesLength: moves.length,
          variantId,
        },
      });
    },
    [
      fen,
      isUserTurn,
      livePremovesEnabled,
      moves.length,
      positionIndex,
      variantId,
    ]
  );

  // Determine if ads should be shown
  const shouldShowAds = tier !== REGULAR_TIER && tier !== UNLIMITED_TIER;

  useEffect(() => {
    if (gameId) {
      setGameIdSeen(gameId);
    }
  }, [gameId]);

  useEffect(() => {
    if (isDesktop) {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    setSelectedTab(Tabs.MOVE);
    if (gameId && gameEnded) {
      setEndGamePopup(true);
    } else {
      setEndGamePopup(false);
    }
  }, [gameEnded, gameId]);

  useEffect(() => {
    if (chatMessages.length != chatLength.current) {
      setUnreadChatMessages(chatMessages.length - chatLength.current);
      setSelectedTab(Tabs.CHAT);
      chatLength.current = chatMessages.length;
    }
  }, [chatMessages]);

  useEffect(() => {
    if (!gameId && process.env.NODE_ENV !== "development") {
      const timer = setTimeout(() => {
        router.push("/play");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [gameId, router]);

  function restoreGameScroll() {
    const restore = () => {
      if (gameScrollRef.current) {
        gameScrollRef.current.scrollTop = chatOpenGameScrollTop.current;
      }
      window.scrollTo(0, chatOpenWindowScrollY.current);
    };

    requestAnimationFrame(restore);
    window.setTimeout(restore, 300);
  }

  function handleShowChatWindow(show) {
    if (show) {
      chatOpenGameScrollTop.current = gameScrollRef.current?.scrollTop ?? 0;
      chatOpenWindowScrollY.current = window.scrollY;
    } else {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      restoreGameScroll();
    }

    setShowChatWindow(show);
    setUnreadChatMessages(0);
    chatLength.current = chatMessages.length;
  }

  function renderTab() {
    if (selectedTab == Tabs.MOVE) {
      return <Moves />;
    }
    if (selectedTab == Tabs.CHAT) {
      return <GameChat />;
    }
  }

  function renderGameButtons() {
    return gameEnded ? (
      <GameEndedButtons
        popupShown={showEndGamePopup}
        setChatOpened={setChatOpened}
        handleShowChatWindow={handleShowChatWindow}
        unreadChatMessages={unreadChatMessages}
        isDesktop={isDesktop}
      />
    ) : (
      <GameButtons
        setChatOpened={setChatOpened}
        handleShowChatWindow={handleShowChatWindow}
        unreadChatMessages={unreadChatMessages}
        isDesktop={isDesktop}
      />
    );
  }

  function renderGameInfo() {
    return (
      <div
        className={
          "hidden flex-col text-sm bg-white rounded-md border md:flex md:border-b-[3px]"
        }
        style={isDesktop ? { height: boardSize - 202 } : {}}
      >
        <div className="flex font-semibold">
          <div
            onClick={() => setSelectedTab(Tabs.MOVE)}
            className={`flex items-center pl-4 space-x-1 py-3 w-full
              hover:${
                !displayGame ? "opacity-70 cursor-pointer" : "opacity-100"
              } 
              ${
                selectedTab == Tabs.MOVE
                  ? `border-b-4 border-b-altblue-300`
                  : "border-b-4 border-b-gray-200 text-altgray-600"
              }`}
          >
            <div>{t("Moves")}</div>
          </div>
          {!displayGame && (
            <div
              onClick={() => setSelectedTab(Tabs.CHAT)}
              className={`flex items-center pl-4 space-x-1 py-3 w-full cursor-pointer hover:opacity-70 ${
                selectedTab == Tabs.CHAT
                  ? `border-b-4 border-b-altblue-300 `
                  : "border-b-4 border-b-gray-200 text-altgray-600"
              }`}
            >
              <div>{t("Chat")}</div>
            </div>
          )}
        </div>
        <div className="flex overflow-auto flex-col flex-1 h-auto">
          {renderTab()}
        </div>
        <div className="justify-center py-3 rounded-b-md border-t md:flex">
          {renderGameButtons()}
        </div>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>Chess Hotel</title>
        <meta name="description" content={t("description")} />
        <meta name="keywords" content={t("keywords")} />
      </Head>
      <ReconnectPopup />
      <GameSyncPopup />
      <div
        ref={gameScrollRef}
        className={`flex ${
          chatOpened ? "overflow-hidden" : "overflow-y-auto"
        } relative flex-col flex-1 py-2 w-full min-h-0 md:flex-row-reverse md:overflow-hidden md:pb-5 md:py-5 md:justify-center lg:flex lg:justify-center`}
      >
        {chatOpened && (
          <ChatWindow
            title={t("Chat with %player", { player: opponentUsername })}
            onClose={() => handleShowChatWindow(false)}
            onAnimationEnd={(e) => {
              if (e.animationName === styles.slideOutHam) {
                setChatOpened(false);
                restoreGameScroll();
              }
            }}
            customClass={
              showChatWindow ? styles.slideInHam : styles.slideOutHam
            }
          >
            <GameChat autoFocusInput={false} />
          </ChatWindow>
        )}
        <div
          className={
            "flex flex-row space-x-2 w-full min-w-0 md:flex-col md:space-x-0 md:mx-2 md:space-y-3 md:w-80 lg:w-[300px]"
          }
        >
          {(!newPlayer || isDesktop) && (
            <div className="flex flex-col w-full min-w-0 md:w-auto md:rounded-md md:border-b-[3px]">
              <OpponentPlayer />
            </div>
          )}
          {isDesktop && renderGameInfo()}
          {(!newPlayer || isDesktop) && (
            <div className="flex flex-col w-full md:w-auto min-w-0 md:rounded-md md:border-b-[3px]">
              <UserPlayer />
            </div>
          )}
        </div>

        {newPlayer && !isDesktop && <OpponentPlayer />}
        <div className="border-y border-altgray-200 md:border-0">
          <span className="flex flex-col md:pr-3">
            <Board
              key={gameId}
              size={boardSize}
              fen={fen}
              lastMove={moves && moves[positionIndex - 1]}
              onMoveMade={sendUserMove}
              autoQueening={autoQueening}
              playingWhite={playingWhite}
              possibleMoves={possibleMoves}
              settings={settings}
              allowPremoves={livePremovesEnabled}
              premove={positionIndex === moves.length ? premove : null}
              onPremoveSet={handlePremoveSet}
              onPremoveClear={clearPremove}
              showInitialPosition={
                !displayGame && variantId === VariantTypes.VARIANT_CHESS
              }
            />
          </span>
        </div>

        {newPlayer && !isDesktop && <UserPlayer />}
        {displayGame && <GameInfo />}
        {/* Mobile game buttons - independently scrollable */}
        <div className="flex flex-col py-4 md:hidden">
          {renderGameButtons()}
        </div>

        {shouldShowAds && isDesktop && <GameAd isDesktop={isDesktop} />}
        {showEndGamePopup && !displayGame && (
          <PopupContainer
            isDesktop={isDesktop}
            onClose={() => setEndGamePopup(false)}
            gameId={gameId}
            result={result}
            status={status}
            variantId={variantId}
            playingWhite={playingWhite}
            leaguePlay={leaguePlay}
            leagueScoreResult={leagueScoreResult}
            moves={moves}
          />
        )}
      </div>
      {shouldShowAds && !isDesktop && <GameAd isDesktop={isDesktop} />}
    </>
  );
}
